import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react'
import LightboxModelViewer from './LightboxModelViewer'

const jloStamp = 'https://jake.surf/assets/jlo-stamp.png'
const ASSETS_BASE = 'https://jake.surf/assets/ceramics-page'

const isAvailable = (metadata) => {
  const c = metadata && metadata.stripeCheckoutDetails
  return !!(c && c.available === true && c.price && c.stripePaymentLink)
}

const PurchaseUI = ({ metadata }) => {
  const c = metadata && metadata.stripeCheckoutDetails
  if (c && c.available === true && c.price && c.stripePaymentLink) {
    return (
      <a
        href={c.stripePaymentLink}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          display: 'inline-block',
          background: 'linear-gradient(135deg, rgb(99,91,255) 0%, #662c94 100%)',
          color: 'rgb(255,255,255)',
          padding: '10px 22px',
          borderRadius: 6,
          textDecoration: 'none',
          fontSize: 15,
          letterSpacing: '0.2px',
          boxShadow: 'rgba(99,91,255,0.45) 7px -7px 20px 0px, rgba(255,255,255,0.15) 0px 1px 0px 0px inset'
        }}>
        Buy · ${c.price}
      </a>
    )
  }
  return <span style={{ color: 'rgba(255,255,255,0.35)', fontSize: 14 }}>Unavailable</span>
}

const arBlue = '#2563eb'


const jsonContext = require.context('./assets/ceramics-page/', true, /metadata\.json$/)

function enc(filename) {
  return filename.split('/').map(encodeURIComponent).join('/')
}

function buildItems(category) {
  const items = {}

  jsonContext.keys().forEach((key) => {
    if (!key.startsWith(`./${category}/`)) return
    const parts = key.split('/')
    if (parts.length !== 4) return // skip category-level metadata.json
    const folder = parts[2]
    const metadata = jsonContext(key)
    const base = `${ASSETS_BASE}/${category}/${folder}`

    const all = (metadata.images || []).map((f) => `${base}/${enc(f)}`)
    const thumbnails = (metadata.images || []).map((f) => `${base}/scaled-down-images/${enc(f)}`)
    const main = metadata.mainImage ? `${base}/main/${enc(metadata.mainImage)}` : (all[0] || null)
    const gridMain = metadata.mainImage ? `${base}/scaled-grid-images/main/${enc(metadata.mainImage)}` : null

    const model =
      metadata.modelGlb
        ? {
            glb: `${base}/model/${enc(metadata.modelGlb)}`,
            usdz: metadata.modelUsdz ? `${base}/model/${enc(metadata.modelUsdz)}` : null
          }
        : null

    const video = metadata.video ? `${base}/video/${enc(metadata.video)}` : null

    items[folder] = { main, gridMain, all, thumbnails, metadata, model, video }
  })

  return Object.keys(items)
    // Order by the `position` field; fall back to folder number for any item
    // missing it (e.g. before the one-time backfill).
    .sort((a, b) => {
      const pa = items[a].metadata?.position ?? parseInt(a.split('-')[1], 10)
      const pb = items[b].metadata?.position ?? parseInt(b.split('-')[1], 10)
      return pa - pb
    })
    .map((folder) => items[folder])
}

const vaseItems = buildItems('vases')
const bowlItems = buildItems('bowls')
const cupItems = buildItems('cups')
const mugItems = buildItems('mugs')
const pitcherItems = buildItems('pitchers')
const plateItems = buildItems('plates')

const styles = {
  page: {
    background: '#fff',
    minHeight: '100vh',
    padding: 16,
    fontFamily: "'Cormorant Garamond', serif"
  },
  sectionTitle: {
    paddingBottom: 12,
    fontSize: 11,
    letterSpacing: '3px',
    textTransform: 'uppercase',
    fontWeight: 600,
    paddingLeft: 5,
    marginTop: 24,
    color: '#999',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: 8
  },
  imgWrapper: {
    position: 'relative',
    width: '100%',
    paddingBottom: '100%',
    overflow: 'hidden',
    borderRadius: 4,
    background: '#f0f0f0',
    cursor: 'pointer'
  },
  img: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover'
  },
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.9)',
    zIndex: 1000,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center'
  },
  lightboxImg: {
    maxWidth: '90vw',
    maxHeight: '80vh',
    objectFit: 'contain',
    borderRadius: 4,
    WebkitTransform: 'translateZ(0)',
    transform: 'translateZ(0)'
  },
  lightboxNav: {
    display: 'flex',
    alignItems: 'center',
    gap: 24,
    marginTop: 16
  },
  lightboxBtn: {
    background: 'none',
    border: '1px solid rgba(255,255,255,0.3)',
    color: '#fff',
    fontSize: 24,
    padding: '8px 16px',
    cursor: 'pointer',
    borderRadius: 4
  },
  lightboxClose: {
    position: 'absolute',
    top: 16,
    right: 16,
    background: 'none',
    border: 'none',
    color: '#fff',
    fontSize: 32,
    cursor: 'pointer',
    zIndex: 1001
  },
  lightboxCounter: {
    color: 'rgba(255,255,255,0.6)',
    fontSize: 14
  },
  thumbnailStrip: {
    display: 'flex',
    gap: 6,
    marginTop: 16,
    overflowX: 'auto',
    maxWidth: '90vw',
    paddingBottom: 4
  },
  thumbnail: {
    width: 56,
    height: 56,
    minWidth: 56,
    objectFit: 'cover',
    borderRadius: 3,
    cursor: 'pointer',
    opacity: 0.5,
    border: '2px solid transparent',
    transition: 'opacity 0.15s, border-color 0.15s'
  },
  thumbnailActive: {
    opacity: 1,
    border: '2px solid #fff'
  }
}

function parseSize(metadata) {
  if (!metadata) return null
  if (metadata.dimensions) {
    const m = metadata.dimensions.match(/([\d.]+)"W\s*x\s*([\d.]+)"H/)
    if (m) return parseFloat(m[1]) * parseFloat(m[2])
  }
  if (metadata.holds) {
    const m = metadata.holds.match(/([\d.]+)/)
    if (m) return parseFloat(m[1])
  }
  return null
}

function parsePrice(metadata) {
  return metadata?.stripeCheckoutDetails?.price ?? null
}

function getKey(field, metadata) {
  if (field === 'size') return parseSize(metadata)
  if (field === 'price') return parsePrice(metadata)
  return null
}

function applySortAndFilter(items, sortBy, filterMode) {
  let result = filterMode === 'available' ? items.filter((item) => isAvailable(item.metadata)) : items
  if (sortBy === 'none') return result
  const [field, dir] = sortBy.split('-')
  return [...result].sort((a, b) => {
    if (field === 'price') {
      const aAvail = isAvailable(a.metadata)
      const bAvail = isAvailable(b.metadata)
      if (aAvail && !bAvail) return -1
      if (!aAvail && bAvail) return 1
    }
    const keyA = getKey(field, a.metadata)
    const keyB = getKey(field, b.metadata)
    if (keyA == null && keyB == null) return 0
    if (keyA == null) return 1
    if (keyB == null) return -1
    return dir === 'desc' ? keyB - keyA : keyA - keyB
  })
}

const SORT_OPTIONS = ['size', 'price', 'none']
const SORT_LABELS = { size: 'Size', price: 'Price', none: 'None' }

const SortControls = ({ sort, onSort }) => {
  const activeField = sort === 'none' ? 'none' : sort.split('-')[0]
  const activeDir = sort === 'none' ? null : sort.split('-')[1]
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontFamily: "'Cormorant Garamond', serif", letterSpacing: '1px', fontSize: 14, fontWeight: 400, textTransform: 'none' }}>
      {/* Muted prefix label so "Sort:" introduces the options instead of competing with them */}
      <span style={{ fontSize: 14, letterSpacing: '1px', color: '#999', marginRight: 2 }}>Sort:</span>
      {SORT_OPTIONS.map((s, i) => {
        const isActive = activeField === s
        const arrow = isActive && s !== 'none' ? (activeDir === 'desc' ? ' ↓' : ' ↑') : ''
        return (
          <React.Fragment key={s}>
            <button
              type="button"
              onClick={() => onSort(s)}
              style={{
                background: 'none',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                fontFamily: "'Cormorant Garamond', serif",
                fontSize: 14,
                letterSpacing: '1px',
                color: '#000',
                textDecoration: isActive ? 'underline' : 'none',
                fontWeight: isActive ? 600 : 400
              }}>
              {SORT_LABELS[s]}{arrow}
            </button>
            {i < SORT_OPTIONS.length - 1 && <span style={{ color: '#ddd' }}>|</span>}
          </React.Fragment>
        )
      })}
    </div>
  )
}

const mediaQuery = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400&display=swap');
  .ceramics-nav-link { color: #555; text-decoration: underline; }
  .ceramics-nav-link:hover { color: #000; }
  @media (max-width: 599px) {
    .ceramics-nav-row { flex-direction: column; align-items: flex-start !important; gap: 8px; }
  }
  @media (min-width: 600px) {
    .ceramics-grid { grid-template-columns: repeat(3, 1fr) !important; gap: 12px !important; }
  }
  @media (min-width: 900px) {
    .ceramics-grid { grid-template-columns: repeat(4, 1fr) !important; gap: 16px !important; }
  }
  .lightbox-content::-webkit-scrollbar { width: 4px; height: 4px; }
  .lightbox-content::-webkit-scrollbar-track { background: transparent; }
  .lightbox-content::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.15); border-radius: 2px; }
  .lightbox-content::-webkit-scrollbar-thumb:hover { background: rgba(255,255,255,0.3); }
  .lightbox-thumbstrip::-webkit-scrollbar { height: 3px; }
  .lightbox-thumbstrip::-webkit-scrollbar-track { background: transparent; }
  .lightbox-thumbstrip::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.15); border-radius: 2px; }
  .lightbox-thumbstrip::-webkit-scrollbar-thumb:hover { background: rgba(255,255,255,0.3); }
`

const isMobile = window.matchMedia('(max-width: 599px)').matches
const isIOS =
  /iP(hone|od|ad)/.test(navigator.userAgent) &&
  /WebKit/.test(navigator.userAgent) &&
  !/(CriOS|FxiOS|OPiOS|mercury)/.test(navigator.userAgent)

const Lightbox = ({ images, thumbnails, metadata, model, video, startIndex, onClose }) => {
  const initialSlot = model ? 'model' : video ? 'video' : (startIndex || 0)
  const [activeSlot, setActiveSlot] = useState(initialSlot)

  const thumbSrcs = thumbnails.length ? thumbnails : images

  const slots = useMemo(
    () => [...(model ? ['model'] : []), ...(video ? ['video'] : []), ...images.map((_, i) => i)],
    [model, video, images]
  )

  const prevSlot = useCallback(() => {
    setActiveSlot((s) => {
      const idx = slots.indexOf(s)
      return slots[idx > 0 ? idx - 1 : slots.length - 1]
    })
  }, [slots])

  const nextSlot = useCallback(() => {
    setActiveSlot((s) => {
      const idx = slots.indexOf(s)
      return slots[idx < slots.length - 1 ? idx + 1 : 0]
    })
  }, [slots])

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose()
      if (e.key === 'ArrowLeft') prevSlot()
      if (e.key === 'ArrowRight') nextSlot()
    }
    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [onClose, prevSlot, nextSlot])

  return (
    <div style={styles.overlay} onClick={onClose}>
      <button style={styles.lightboxClose} onClick={onClose}>
        &times;
      </button>
      <div
        className="lightbox-content"
        onClick={(e) => e.stopPropagation()}
        style={{ maxHeight: '95vh', overflowY: 'auto', paddingRight: 12 }}>
        {activeSlot === 'video' && video ? (
          <video
            key={video}
            src={video}
            autoPlay
            muted
            controls
            playsInline
            style={{ maxWidth: '90vw', maxHeight: '80vh', borderRadius: 4, display: 'block', background: '#000' }}
          />
        ) : activeSlot === 'model' && model ? (
          <LightboxModelViewer
            src={model.glb}
            iosSrc={model.usdz}
            style={{ width: '90vw', maxWidth: 600, height: '60vh', borderRadius: 4, background: '#222' }}
          />
        ) : (
          <img src={images[typeof activeSlot === 'number' ? activeSlot : 0]} alt="" style={styles.lightboxImg} />
        )}
        {typeof activeSlot === 'number' && (
          <div style={styles.lightboxNav}>
            <button style={styles.lightboxBtn} onClick={prevSlot}>
              &#8249;
            </button>
            <span style={styles.lightboxCounter}>
              {activeSlot + 1} / {images.length}
            </span>
            <button style={styles.lightboxBtn} onClick={nextSlot}>
              &#8250;
            </button>
          </div>
        )}
        <div className="lightbox-thumbstrip" style={styles.thumbnailStrip}>
          {model && (
            <div
              style={{
                ...styles.thumbnail,
                background: '#1a1a2e',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
                ...(activeSlot === 'model' ? styles.thumbnailActive : {})
              }}
              onClick={() => setActiveSlot('model')}>
              <span style={{ fontSize: 16 }}>👀</span>
              <span style={{ color: '#fff', fontSize: 8, marginTop: 2 }}>3D/AR</span>
            </div>
          )}
          {video && (
            <div
              style={{
                ...styles.thumbnail,
                background: '#111',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
                ...(activeSlot === 'video' ? styles.thumbnailActive : {})
              }}
              onClick={() => setActiveSlot('video')}>
              <div
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  background: 'rgba(255,255,255,0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                <div
                  style={{
                    width: 0,
                    height: 0,
                    borderStyle: 'solid',
                    borderWidth: '6px 0 6px 11px',
                    borderColor: 'transparent transparent transparent #fff',
                    marginLeft: 2
                  }}
                />
              </div>
            </div>
          )}
          {thumbSrcs.map((src, i) => (
            <img
              key={i}
              src={src}
              alt=""
              style={{ ...styles.thumbnail, ...(activeSlot === i ? styles.thumbnailActive : {}) }}
              onClick={() => setActiveSlot(i)}
            />
          ))}
        </div>
        {activeSlot === 'model' && model && (
          <div style={{ marginTop: 8, color: '#ff4444', fontSize: 14, textAlign: 'center' }}>
            {isIOS
              ? 'Tap the AR cube to view the piece in your space.'
              : 'Open this page on your iPhone in Safari to see the piece in your space using AR.'}
          </div>
        )}
        {metadata && (
          <div style={{ marginTop: 16, maxWidth: '90vw', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16 }}>
            <div>
              {Object.entries(metadata)
                .filter(([key]) => !['position', 'stripeCheckoutDetails', 'images', 'mainImage', 'modelGlb', 'modelUsdz', 'video'].includes(key))
                .map(([key, value]) => (
                  <div key={key} style={{ color: '#fff', fontSize: 14, lineHeight: 1.6 }}>
                    {key === 'holds' ? `Holds ${value}` : value}
                  </div>
                ))}
            </div>
            <div style={{ flexShrink: 0 }}>
              <PurchaseUI metadata={metadata} />
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

const ImageGrid = ({ items, onItemClick }) => (
  <div className="ceramics-grid" style={styles.grid}>
    {items.map((item, i) => (
      <div key={i} style={styles.imgWrapper} onClick={() => onItemClick(item.all, item.thumbnails, item.metadata, item.model, item.video)}>
        <img src={isMobile && item.gridMain ? item.gridMain : item.main} alt="" style={styles.img} loading="lazy" />
        {isAvailable(item.metadata) && (
          <div
            style={{
              position: 'absolute',
              bottom: 6,
              right: 6,
              width: 10,
              height: 10,
              borderRadius: '50%',
              background: '#22c55e',
              boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
            }}
          />
        )}
        {item.model && (
          <div
            style={{
              position: 'absolute',
              top: 14,
              right: -32,
              background: arBlue,
              color: '#fff',
              fontSize: 10,
              fontWeight: 'bold',
              padding: '4px 40px',
              letterSpacing: 0.8,
              textTransform: 'uppercase',
              transform: 'rotate(45deg)',
              boxShadow: '0 2px 4px rgba(0,0,0,0.3)'
            }}>
            👀 3D/AR
          </div>
        )}
      </div>
    ))}
  </div>
)

export const CeramicsPage = () => {
  const [lightboxData, setLightboxData] = useState(null)
  const [filter, setFilter] = useState('all')
  const [activeSort, setActiveSort] = useState('none')
  const [isStuck, setIsStuck] = useState(false)
  const sentinelRef = useRef(null)

  useEffect(() => {
    const sentinel = sentinelRef.current
    if (!sentinel) return
    const observer = new IntersectionObserver(
      ([entry]) => setIsStuck(!entry.isIntersecting),
      { threshold: [1] }
    )
    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [])

  const handleSort = useCallback((field) => {
    setActiveSort((prev) => {
      if (field === 'none') return 'none'
      const isGlobal = prev.startsWith('size') || prev.startsWith('price')
      const prevField = isGlobal ? prev.split('-')[0] : null
      const prevDir = isGlobal ? prev.split('-')[1] : null
      if (prevField === field) return `${field}-${prevDir === 'desc' ? 'asc' : 'desc'}`
      return `${field}-desc`
    })
  }, [])

  const getItems = useCallback(
    (items) => applySortAndFilter(items, activeSort, filter),
    [filter, activeSort]
  )

  const openLightbox = useCallback((images, thumbnails, metadata, model, video) => {
    setLightboxData({ images, thumbnails, metadata, model, video })
  }, [])

  const closeLightbox = useCallback(() => {
    setLightboxData(null)
  }, [])

  return (
    <div style={styles.page}>
      <style>{mediaQuery}</style>
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', paddingBottom: 20, paddingTop: 18 }}>
        <img src={jloStamp} alt="JLO" style={{ height: 48, width: 'auto', objectFit: 'contain' }} />
      </div>

      <div style={{ paddingBottom: 8, display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
        {[
          { href: '#bowls', text: 'Bowls' },
          { href: '#mugs', text: 'Mugs' },
          { href: '#vases', text: 'Vases' },
          { href: '#pitchers', text: 'Pitchers' },
          { href: '#plates', text: 'Plates' },
          { href: '#cups', text: 'Cups' }
        ].map((w, idx, arr) => (
          <React.Fragment key={idx}>
            <div style={{ fontSize: 15 }}>
              <a href={w.href} className="ceramics-nav-link">{w.text}</a>
            </div>
            {idx !== arr.length - 1 && <div style={{ color: '#ccc', fontSize: 15, margin: '0 3px' }}>|</div>}
          </React.Fragment>
        ))}
      </div>

      <div style={{ textAlign: 'center', fontSize: 13, color: '#999', paddingBottom: 12, fontStyle: 'italic' }}>
        All are food safe and top-shelf dishwasher safe.
      </div>

      <div ref={sentinelRef} style={{ height: 1 }} />
      <div style={{
        position: 'sticky',
        top: 0,
        zIndex: 100,
        background: '#fff',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingLeft: 5,
        paddingRight: 5,
        paddingTop: 10,
        paddingBottom: 10,
        borderBottom: isStuck ? '1px solid #000' : 'none',
        boxShadow: isStuck ? '0 8px 16px rgba(0,0,0,0.18)' : 'none',
        clipPath: isStuck ? 'inset(0 0 -30px 0)' : 'none',
        marginLeft: -16,
        marginRight: -16,
        paddingLeft: 18,
        paddingRight: 18,
        marginBottom: 4
      }}>
        <button
          type="button"
          onClick={() => setFilter((f) => (f === 'all' ? 'available' : 'all'))}
          style={{
            background: 'transparent',
            border: '1px solid #000',
            padding: '4px 12px',
            cursor: 'pointer',
            fontSize: 14,
            color: '#000',
            fontFamily: "'Cormorant Garamond', serif"
          }}>
          {filter === 'all' ? 'Hide Unavailable' : 'Show Unavailable'}
        </button>
        <SortControls sort={activeSort} onSort={handleSort} />
      </div>

      {[
        { id: 'bowls', label: 'Bowls', items: bowlItems, mt: 14 },
        { id: 'mugs', label: 'Mugs', items: mugItems },
        { id: 'vases', label: 'Vases', items: vaseItems },
        { id: 'pitchers', label: 'Pitchers', items: pitcherItems },
        { id: 'plates', label: 'Plates', items: plateItems },
        { id: 'cups', label: 'Cups', items: cupItems },
      ].map(({ id, label, items, mt }) => (
        <React.Fragment key={id}>
          <div id={id} style={{ ...styles.sectionTitle, ...(mt ? { marginTop: mt } : {}) }}>
            {label}
          </div>
          <ImageGrid items={getItems(items)} onItemClick={openLightbox} />
        </React.Fragment>
      ))}
      <div style={{ textAlign: 'center', fontSize: 13, color: '#999', paddingTop: 20, fontStyle: 'italic' }}>
        <span style={{ display: 'block' }}><span style={{ fontStyle: 'normal' }}>&#128236;</span> Send inquiries to jaloeterman at gmail dot com</span>
        <span style={{ display: 'block' }}>or DM me @darkpano on Instagram</span>
      </div>
      <div style={{ marginTop: 16, paddingBottom: 16, color: '#aaa', fontSize: 13, textAlign: 'center' }}>
        Copyright &copy; 2026 Jake Loeterman
      </div>

      {lightboxData && (
        <Lightbox
          images={lightboxData.images}
          thumbnails={lightboxData.thumbnails}
          metadata={lightboxData.metadata}
          model={lightboxData.model}
          video={lightboxData.video}
          startIndex={0}
          onClose={closeLightbox}
        />
      )}
    </div>
  )
}
